import { Button, Col, Modal, Row, Typography } from 'antd';
import styles from './index.module.less';
import { useMethods } from '@lhb/hook';
import { FC, useEffect, useRef, useState } from 'react';
import SearchInMap from '@/common/components/business/SearchInMap';
import V2AMap from '@/common/components/Map/V2AMap';
import { useAmapLevelAndCityNew } from '@/common/hook/useAmapLevelAndCityNew';
import { getTargetPOIInfo } from '@/common/api/spotevaluation';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { isNotEmpty, isNotEmptyAny } from '@lhb/func';
import { useCurLocation } from '@/common/hook/Amap/useCurLocation';

/**
 * @Description 门店编辑调整地址
 */
const MapAddressChangeModal: FC<{
  open,
  setOpen,
  form,
  mustSameDistrict?, // 是否启用“门店微调不得跨区”的限制。最初门店签约、编辑时开启，目前已停用
  setAddressInfo
}> = ({
  open,
  setOpen,
  form,
  mustSameDistrict = false,
  setAddressInfo,
}) => {
  const centerMarkerRef = useRef<any>(null);
  const [amapIns, setAmapIns] = useState<any>(null);
  const { level, city, district } = useAmapLevelAndCityNew(amapIns, false, true);
  const [poiInfo, setPoiInfo] = useState<any>({}); // 当前选择的poi信息
  const [defaultDistrict, setDefaultDistrict] = useState<any>(''); // 默认的区信息
  const selPoiExtraDataRef: any = useRef(null); // 手动选择的下拉poi数据

  const locationInfo = useCurLocation(amapIns, {
    zoomToAccuracy: false, // 获取到定位后是否缩放地图
    panToLocation: false, // 定位成功后是否自动移动到响应位置
    showCircle: false,
  });
  const locationInfoRef: any = useRef(locationInfo);

  useEffect(() => {
    locationInfoRef.current = locationInfo;
    const { lng, lat } = locationInfo || {};
    if (+lng && +lat && !mustSameDistrict) { // 可自由选择跨行政区的位置时，才使用定位位置
      const _position = form?.getFieldValue('position');
      const splitPosition = _position?.split(',') || [];
      // 如果已经选择过位置，不使用定位位置填充地址
      if (splitPosition?.length === 2) return;

      methods.locatedPositionHandle({
        lng: +lng,
        lat: +lat
      });
    }
  }, [locationInfo]);

  useEffect(() => {
    if (open && amapIns) {
      methods.initHandle();
    }
  }, [open, amapIns]);

  useEffect(() => {
    if (!amapIns) return;
    amapIns.on('click', methods.handleMapClick); // 点击

    return () => {
      amapIns.on('off', methods.handleMapClick); // 解绑
    };
  }, [amapIns]);

  const methods = useMethods({
    mapLoadedHandle: (mapIns: any) => {
      setAmapIns(mapIns);
    },
    async initHandle() {
      const _position = form.getFieldValue('position');
      const _address = form.getFieldValue('address');
      const splitPosition = _position?.split(',') || [];

      let _provinceId;
      let _cityId;
      let _districtId;
      if (splitPosition?.length === 2) {
        const lngLat = {
          lng: splitPosition[0],
          lat: splitPosition[1]
        };
        methods.setCenterMarker(lngLat);
        const data = await getTargetPOIInfo(lngLat);
        setDefaultDistrict(data.districtName);
        _provinceId = data?.provinceId;
        _cityId = data?.cityId;
        _districtId = data?.districtId;
      } else {
        const { lng, lat } = locationInfoRef.current || {};
        if (+lng && +lat && !mustSameDistrict) { // 可自由选择跨行政区的位置时，才使用定位位置
          methods.locatedPositionHandle({
            lng: +lng,
            lat: +lat
          });
        }
      }
      setPoiInfo({
        address: _address,
        lng: splitPosition?.[0] || '',
        lat: splitPosition?.[1] || '',
        provinceId: _provinceId,
        cityId: _cityId,
        districtId: _districtId,
      });

    },
    async locatedPositionHandle(lngLat) {
      methods.setCenterMarker(lngLat);
      const data = await getTargetPOIInfo(lngLat);
      if (!data) return;
      const { provinceName, cityName, districtName, provinceId, cityId, districtId, latitude, longitude, address } = data;
      if (!address) return;

      +latitude && +longitude && setPoiInfo({
        address: `${provinceName || ''}${cityName || ''}${districtName || ''}${address || ''}`,
        lng: +longitude,
        lat: +latitude,
        provinceName,
        cityName,
        districtName,
        provinceId,
        cityId,
        districtId,
      });
    },
    setCenterMarker(lnglat) {
      const { lng, lat } = lnglat;
      const targetLngLat = [lng, lat];
      amapIns.setCenter(targetLngLat, true);
      if (centerMarkerRef.current) {
        centerMarkerRef.current.setPosition(targetLngLat); // 更新中心点位置
        amapIns.setFitView(centerMarkerRef.current);
        return;
      }
      const size = new window.AMap.Size(30, 30);
      const customIcon = new window.AMap.Icon({
        // 图标尺寸
        size,
        // 图标的取图地址
        image: `${process.env.STATIC_RES_DOMAIN}/project-custom/location-insight/icon_marker_point@2x.png`,
        // 图标所用图片大小
        imageSize: size,
        // 图标取图偏移量
      });
      const centerMarker = new window.AMap.Marker({
        position: targetLngLat,
        // anchor: 'center', // 底部中间为基准点
        icon: customIcon,
        offset: new window.AMap.Pixel(-15, -15),
        // zIndex: 30
        // zooms: [13, 20]
      });
      amapIns.add(centerMarker);
      amapIns.setFitView(centerMarker);
      centerMarkerRef.current = centerMarker;
    },
    handleMapClick: (e: any) => {
      const { lnglat: center } = e || {};
      const { lng, lat } = center || {};
      if (!(+lng && +lat)) return;
      amapIns.setCenter(center, true);
      methods.loadPOIData({
        lng: +lng,
        lat: +lat
      });
    },
    loadPOIData: async (lnglat: any) => {
      const data = await getTargetPOIInfo(lnglat);
      if (!data) return;
      const { provinceName, cityName, districtName, provinceId, cityId, districtId, latitude, longitude, address } = data;
      // 没有地址，没有城市，基本上在大海里
      if (!address) {
        V2Message.warning('未查询到对应的POI点位');
        return;
      }
      +latitude && +longitude && setPoiInfo({
        lng: +longitude,
        lat: +latitude,
        address: selPoiExtraDataRef.current?.address || `${provinceName}${cityName}${districtName || ''}${address}`, // 手动选择poi时优先使用poi，移动地图时使用地址解析结果
        provinceName,
        cityName,
        districtName,
        provinceId,
        cityId,
        districtId,
      });
      // 绘制地图覆盖物
      methods.setCenterMarker(lnglat);
      selPoiExtraDataRef.current = null; // 用完清空
    },
    mapSearchChange(val) {
      if (isNotEmptyAny(val?.extraData?.location)) {
        selPoiExtraDataRef.current = val?.extraData; // 暂存手动选择poi数据作为当前地址
        amapIns.setCenter(val.extraData?.location, true);
        methods.loadPOIData(val.extraData.location);
      }
    },
    confirmMapAddress() {
      if (!poiInfo.districtId) { // 区名存在为空情况，改为判断区id
        // 不存在则认定为未更新过地址，直接关闭
        setOpen(false);
        return;
      }
      if (mustSameDistrict && isNotEmptyAny(poiInfo) && defaultDistrict && poiInfo.districtName !== defaultDistrict) {
        V2Message.warning('调整后的位置与之前相差过大，请重新选择');
        return;
      }
      if (isNotEmptyAny(poiInfo)) {
        form.setFieldValue('address', poiInfo.address);
        form.setFieldValue('position', `${poiInfo.lng},${poiInfo.lat}`);
        form.validateFields(['address', 'position']);

        if (!mustSameDistrict) { // 省市区允许变动，比如创建场景，回填省市区名称
          form.setFieldValue('provinceName', poiInfo.provinceName);
          form.setFieldValue('cityName', poiInfo.cityName);
          form.setFieldValue('districtName', poiInfo.districtName || poiInfo.cityName); // 没区的特殊场景用城市名兼容，比如东莞
        }
        setAddressInfo?.(poiInfo);
      }
      setOpen(false);
    }
  });

  return (
    <Modal
      open={open}
      title='选择地址'
      onCancel={() => setOpen(false)}
      width={864}
      bodyStyle={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 0,
        paddingTop: 8,
      }}
      footer={
        <div className={styles.footerContent}>
          <Row gutter={16} className={styles.footerLeft}>
            <Col span={12}>
              <div className={styles.detailItemContent}>
                <div className={styles.detailItemLabel}>详细地址</div>
                <div className={styles.detailItemValue}>
                  <Typography.Text
                    style={{ width: '100%' }}
                    ellipsis={{ tooltip: poiInfo.address }}>
                    <span>
                      { poiInfo.address }
                    </span>
                  </Typography.Text>
                </div>
              </div>
            </Col>
            <Col span={12}>
              <div className={styles.detailItemContent}>
                <div className={styles.detailItemLabel}>经纬度</div>
                <div className={styles.detailItemValue}>{isNotEmpty(poiInfo.lng) && isNotEmpty(poiInfo.lat) ? `${poiInfo.lng},${poiInfo.lat}` : ''}</div>
              </div>
            </Col>
          </Row>
          <Button className={styles.footerRightBtn} type='primary' onClick={methods.confirmMapAddress}>确定</Button>
        </div>
      }
    >
      <div className={styles.modalMapContent}>
        <V2AMap
          mapOpts={{
            zooms: [4, 20],
            // center: [103.826777, 36.060634], // 默认地图的中心位置，使中国地图处于地图正中央
          }}
          loaded={methods.mapLoadedHandle}
          plugins={[
            'AMap.AutoComplete',
          ]}
          styleOpt={{
            width: '100%',
            height: '100%',
          }}
        >
        </V2AMap>
        <div style={{
          position: 'absolute',
          top: '24px',
          left: '24px',
          zIndex: 2,
        }}>
          <SearchInMap
            key={open}
            mapIns={amapIns}
            mapHelpfulInfo={{ level, city, district }}
            hideBorder
            finallySelected={methods.mapSearchChange}
            searchConfig={{
              style: {
                width: 240,
                height: 37
              },
            }}
            showSearchPOIMarker={false}
          />
        </div>
      </div>
    </Modal>
  );
};

export default MapAddressChangeModal;
